import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, Settings, LogOut } from 'lucide-react';
import { authService } from '../services/authService';

interface HeaderProps {
  showAdminControls?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showAdminControls = false }) => {
  const location = useLocation();
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'Prime Utility Company';
  const companyLogo = process.env.REACT_APP_COMPANY_LOGO || '/images/puc-logo.png';

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Company Name */}
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              {companyLogo && (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="h-8 w-8 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              )}
              <div className="flex flex-col">
                <h1 className="text-lg font-semibold text-gray-900 font-english">
                  {companyName}
                </h1>
                <p className="text-xs text-gray-500 font-english">
                  Digital Business Cards
                </p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-primary'
              }`}
            >
              Home
            </Link>
            
            {showAdminControls && (
              <>
                <Link
                  to="/admin"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/admin'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-500 hover:text-primary'
                  }`}
                >
                  <div className="flex items-center space-x-1">
                    <Settings className="h-4 w-4" />
                    <span>Admin</span>
                  </div>
                </Link>
                
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors flex items-center space-x-1"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="text-gray-500 hover:text-primary focus:outline-none focus:text-primary"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className="md:hidden hidden">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50">
          <Link
            to="/"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/'
                ? 'text-primary bg-primary bg-opacity-10'
                : 'text-gray-500 hover:text-primary hover:bg-gray-100'
            }`}
          >
            Home
          </Link>
          
          {showAdminControls && (
            <>
              <Link
                to="/admin"
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === '/admin'
                    ? 'text-primary bg-primary bg-opacity-10'
                    : 'text-gray-500 hover:text-primary hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Admin</span>
                </div>
              </Link>
              
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-500 hover:text-red-600 hover:bg-gray-100"
              >
                <div className="flex items-center space-x-2">
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header; 